import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import {
  deleteOrdersFilesByFileName,
  fetchOrdersFiles,
  postOrdersFiles,
} from 'api/requests/requests';
import { STALE_FIVE_MINUTES } from 'api/types';
import { AxiosError } from 'axios';
import { FileResponse } from 'utils/data/data-types';

export const ordersQueryKeys = {
  all: ['orders'] as const,
  detail: (bookingNumber: string) => [...ordersQueryKeys.all, bookingNumber] as const,
  ordersFiles: (bookingNumber: string) =>
    [...ordersQueryKeys.detail(bookingNumber), 'files'] as const,
};

export function useOrdersFilesQuery(bookingNumber: string) {
  return useQuery({
    queryKey: ordersQueryKeys.ordersFiles(bookingNumber),
    queryFn: () => fetchOrdersFiles(bookingNumber),
    staleTime: STALE_FIVE_MINUTES,
    select: ({ data }) => data,
  });
}

export function useDeleteOrdersFilesMutation(bookingNumber: string) {
  const queryClient = useQueryClient();

  return useMutation<number, AxiosError, { fileName: string }>({
    mutationKey: ordersQueryKeys.ordersFiles(bookingNumber),
    mutationFn: (data) =>
      deleteOrdersFilesByFileName(bookingNumber, data.fileName).then((d) => d.status),
    onSuccess: () => {
      // Invalidate orders files
      queryClient.invalidateQueries({ queryKey: ordersQueryKeys.ordersFiles(bookingNumber) });
    },
  });
}

export function usePostOrdersFilesMutation(bookingNumber: string) {
  const queryClient = useQueryClient();

  return useMutation<FileResponse, AxiosError, FormData>({
    mutationKey: ordersQueryKeys.ordersFiles(bookingNumber),
    mutationFn: (data) => postOrdersFiles(bookingNumber, data).then((d) => d.data),
    onSuccess: () => {
      // Invalidate orders files
      queryClient.invalidateQueries({ queryKey: ordersQueryKeys.ordersFiles(bookingNumber) });
    },
  });
}
